import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as api from 'api/methods';

import { copyToClipboard } from 'helpers/copyToClipboard';
import { getTranslation } from 'helpers/getTranslation';
import { showToast } from 'helpers/showToast';

import {
  mapCuratorStudentDetailResponse,
  type CuratorStudentDetailData,
} from './SchoolCuratorStudentPage.helpers';

import SchoolCuratorStudentPage from './SchoolCuratorStudentPage';

const SchoolCuratorStudentPageContainer = () => {
  const navigate = useNavigate();
  const { token, id: schoolId, studentId } = useParams();
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    const previousLang = i18n.language;
    void i18n.changeLanguage('ru');
    return () => {
      void i18n.changeLanguage(previousLang);
    };
  }, [i18n]);

  const isPublic = Boolean(token);

  useEffect(() => {
    if (!isPublic) {
      return;
    }

    const previous = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#f8fafc';

    return () => {
      document.body.style.backgroundColor = previous;
    };
  }, [isPublic]);
  const courseId = searchParams.get('courseId');

  const [loading, setLoading] = useState(false);
  const [detailData, setDetailData] = useState<CuratorStudentDetailData | null>(
    null,
  );

  const backLink = useMemo(() => {
    if (isPublic && token) {
      return `/school-report/${token}`;
    }

    if (schoolId) {
      return `/schools/${schoolId}/curator-dashboard`;
    }

    return null;
  }, [isPublic, schoolId, token]);

  const handleBackClick = useCallback(() => {
    if (backLink) {
      navigate(backLink);
    }
  }, [backLink, navigate]);

  const handleCopyPromocodeClick = useCallback(() => {
    const promocodeCode = detailData?.student.promocodeAccess?.code;

    if (!promocodeCode) {
      return;
    }

    copyToClipboard(promocodeCode);

    showToast({
      data: {
        variant: 'success',
        title: getTranslation('common.toast.clipboard_copy.title'),
        text: getTranslation('common.toast.clipboard_copy.text'),
      },
    });
  }, [detailData?.student.promocodeAccess?.code]);

  const buildCourseLink = useCallback(
    (targetCourseId: string) => {
      if (isPublic && token) {
        return `/school-report/${token}/students/${studentId}?courseId=${targetCourseId}`;
      }

      if (schoolId) {
        return `/schools/${schoolId}/curator-dashboard/students/${studentId}?courseId=${targetCourseId}`;
      }

      return '#';
    },
    [isPublic, schoolId, studentId, token],
  );

  useEffect(() => {
    if (!studentId || !courseId) {
      setDetailData(null);
      return;
    }

    if (isPublic && !token) {
      return;
    }

    if (!isPublic && !schoolId) {
      return;
    }

    let isCancelled = false;

    const loadDetail = async () => {
      setLoading(true);

      try {
        const response = isPublic
          ? await api.getPublicCuratorStudentDetail(
              token as string,
              studentId,
              courseId,
            )
          : await api.getCuratorStudentDetail(
              schoolId as string,
              studentId,
              courseId,
            );

        if (!isCancelled) {
          setDetailData(
            mapCuratorStudentDetailResponse(
              response as Parameters<typeof mapCuratorStudentDetailResponse>[0],
            ),
          );
        }
      } catch (error) {
        console.warn(error);

        if (!isCancelled) {
          setDetailData(null);
        }
      }

      if (!isCancelled) {
        setLoading(false);
      }
    };

    loadDetail();

    return () => {
      isCancelled = true;
    };
  }, [courseId, isPublic, schoolId, studentId, token]);

  return (
    <SchoolCuratorStudentPage
      locale="ru"
      isPublic={isPublic}
      loading={loading}
      detailData={detailData}
      showBackButton={Boolean(backLink)}
      onBackClick={handleBackClick}
      onCopyPromocodeClick={handleCopyPromocodeClick}
      buildCourseLink={buildCourseLink}
      missingCourse={!courseId}
    />
  );
};

export default React.memo(SchoolCuratorStudentPageContainer) as any;
